/**
 * Auth context built on Amplify Auth (Cognito under the hood).
 *
 * Exposes:
 *   - user (current authenticated user) and isLoaded (gates initial render)
 *   - signIn / signUp / confirmSignUp / signOut / resetPassword / confirmResetPassword
 *   - the org context the API needs (org_id, org_role, app_role) — pulled from the JWT claims
 *
 * The /me endpoint is the source of truth for org context post-auth, but we
 * use the JWT claims for fast first paint. They go stale on org-switch (Agency
 * users); the SPA refreshes the token after any org-switch action.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn as ampSignIn,
  signUp as ampSignUp,
  confirmSignUp as ampConfirmSignUp,
  signOut as ampSignOut,
  resetPassword as ampResetPassword,
  confirmResetPassword as ampConfirmResetPassword,
  type AuthUser,
} from 'aws-amplify/auth';
import { configureAmplify } from './amplify-config';
import { analytics } from '@/lib/analytics';

export interface OrgContext {
  orgId: string;
  orgRole: 'owner' | 'admin' | 'analyst';
  appRole: 'user' | 'admin' | 'moderator';
}

export interface AuthContextValue {
  user: AuthUser | null;
  org: OrgContext | null;
  email: string | null;
  isLoaded: boolean;

  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ requiresConfirmation: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  configureAmplify();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [org, setOrg] = useState<OrgContext | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const refresh = async () => {
    try {
      const u = await getCurrentUser();
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload ?? {};
      setUser(u);
      setEmail((claims.email as string | undefined) ?? null);
      const orgId = claims['custom:org_id'] as string | undefined;
      if (orgId) {
        setOrg({
          orgId,
          orgRole: ((claims['custom:org_role'] as string) ?? 'analyst') as OrgContext['orgRole'],
          appRole: ((claims['custom:app_role'] as string) ?? 'user') as OrgContext['appRole'],
        });
      } else {
        setOrg(null);
      }
      // Tie subsequent GA4 events to this user. We use Cognito's userId (sub)
      // — never the email — so the value is opaque + GDPR-safe.
      analytics.identify(u.userId, orgId ? { org_id: orgId } : {});
    } catch {
      setUser(null);
      setOrg(null);
      setEmail(null);
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      org,
      email,
      isLoaded,
      async signIn(emailArg, password) {
        await ampSignIn({ username: emailArg, password });
        await refresh();
        analytics.trackLogin('email');
      },
      async signUp(emailArg, password) {
        const out = await ampSignUp({
          username: emailArg,
          password,
          options: { userAttributes: { email: emailArg } },
        });
        return { requiresConfirmation: out.nextStep?.signUpStep === 'CONFIRM_SIGN_UP' };
      },
      async confirmSignUp(emailArg, code) {
        await ampConfirmSignUp({ username: emailArg, confirmationCode: code });
      },
      async signOut() {
        await ampSignOut();
        setUser(null);
        setOrg(null);
        setEmail(null);
        analytics.revoke();
      },
      async resetPassword(emailArg) {
        await ampResetPassword({ username: emailArg });
      },
      async confirmResetPassword(emailArg, code, newPassword) {
        await ampConfirmResetPassword({ username: emailArg, confirmationCode: code, newPassword });
      },
      refresh,
    }),
    [user, org, email, isLoaded],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
